import * as React from "react";

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import {
  getCommonFieldBasedElementParams,
  useDatasheetElementController,
} from "@modules/datasheets/components/result/controllers/datasheet-element";
import { trDts } from "@modules/datasheets/utils/i18n";
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable } from '../../context';

export type LookupTableOption = {
  value: string | number;
  label: string;
};

export type LookupTableColumn = {
  header: string;
  accessorKey: keyof LookupTableOption;
};

export type LookupTableFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto>;

export const useLookupTableFieldController = (props: LookupTableFieldProps) => {
  const { datasheetElement, onChange } = props;

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const elementId = datasheetElement.id;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isEditable = useDatasheetResultFieldEditable(datasheetElement.original.editable !== false);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(elementId, {
      focus: () => containerRef.current?.querySelector<HTMLButtonElement>('button')?.focus(),
    });
    return () => { registerField(elementId, null); };
  }, [elementId, registerField, isEditable]);

  const handleTabNavigation = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(elementId);
      } else {
        focusNext(elementId);
      }
    },
    [elementId, focusNext, focusPrev],
  );

  const [menuOpened, setMenuOpened] = React.useState(false);
  const menuOpenedRef = React.useRef(menuOpened);
  menuOpenedRef.current = menuOpened;

  const DROPDOWN_PLACEHOLDER = " ";

  const { headingSizeDependencies, headingPlacedHorizontally, headingPosition } =
    getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(props, { headingSizeDependencies });

  const getCurrentValue = () => (datasheetElement.original.field as any).value ?? '';
  const [selectedValue, setSelectedValue] = React.useState<string | number>(getCurrentValue());

  React.useEffect(() => {
    const newValue = getCurrentValue();
    setSelectedValue(prev => prev !== newValue ? newValue : prev);
  }, [datasheetElement.original.field]);

  const normalizeDropdownOptions = React.useMemo<Array<LookupTableOption>>(() => {
    const lookupValues = (datasheetElement.original.field as any).lookupValues;
    
    if (!lookupValues || typeof lookupValues !== 'object') {
      return [];
    }

    const columnKeys = Object.keys(lookupValues);
    if (columnKeys.length === 0) {
      return [];
    }

    const firstKey = columnKeys[0];
    const secondKey = columnKeys.length > 1 ? columnKeys[1] : undefined;
    if (!firstKey) {
      return [];
    }

    const valueColumn = lookupValues[firstKey] || [];
    const labelColumn = secondKey ? (lookupValues[secondKey] || []) : valueColumn;

    if (!Array.isArray(valueColumn) || !Array.isArray(labelColumn)) {
      return [];
    }

    const maxLength = Math.max(valueColumn.length, labelColumn.length);
    const options: Array<LookupTableOption> = [];

    for (let i = 0; i < maxLength; i++) {
      const value = valueColumn[i];
      const label = labelColumn[i] ?? valueColumn[i] ?? '';
      
      if (value !== undefined && value !== null) {
        options.push({
          value: String(value),
          label: String(label),
        });
      }
    }

    return options;
  }, [datasheetElement.original.field]);

  const selectedItem = React.useMemo<LookupTableOption | undefined>(
    () => normalizeDropdownOptions.find(({ value }) => String(value) === String(selectedValue)),
    [normalizeDropdownOptions, selectedValue]
  );

  const columns: Array<LookupTableColumn> = React.useMemo(() => {
    const lookupValues = (datasheetElement.original.field as any).lookupValues;
    
    if (lookupValues && typeof lookupValues === 'object') {
      const columnKeys = Object.keys(lookupValues);
      const firstKey = columnKeys[0];
      const secondKey = columnKeys[1];
      
      if (columnKeys.length >= 2 && firstKey && secondKey) {
        return [
          { header: firstKey, accessorKey: "value" },
          { header: secondKey, accessorKey: "label" },
        ];
      } else if (columnKeys.length === 1 && firstKey) {
        return [
          { header: firstKey, accessorKey: "value" },
          { header: firstKey, accessorKey: "label" },
        ];
      }
    }

    return [
      { header: trDts("State"), accessorKey: "value" },
      { header: trDts("StTerrProvName"), accessorKey: "label" },
    ];
  }, [datasheetElement.original.field]);

  const handleOpenMenu = React.useCallback(() => {
    setMenuOpened(true);
    requestAnimationFrame(() => {
      containerRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    });
  }, []);

  const handleCloseMenu = React.useCallback(() => {
    setMenuOpened(false);
  }, []);

  const handleButtonClick = React.useCallback(() => {
    if (menuOpenedRef.current) {
      handleCloseMenu();
    } else {
      handleOpenMenu();
    }
  }, [handleCloseMenu, handleOpenMenu]);

  const handleLookupTableFieldValueChange = React.useCallback(
    (value: LookupTableOption["value"]) => {
      handleCloseMenu();
      setSelectedValue(value);
      onChange(datasheetElement.id, {
        ...datasheetElement,
        original: {
          ...datasheetElement.original,
          field: {
            ...datasheetElement.original.field,
            value,
          },
        },
      });
    },
    [datasheetElement, onChange, handleCloseMenu]
  );

  return {
    isEditable,
    handleLookupTableFieldValueChange,
    handleButtonClick,
    handleCloseMenu,
    headingPosition,
    headingPlacedHorizontally,
    menuOpened,
    columns,
    selectedItem,
    normalizeDropdownOptions,
    DROPDOWN_PLACEHOLDER,
    containerRef,
    handleTabNavigation,
    ...baseDatasheetElementController,
  };
};
